import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { ArrowUp, ChevronDown, ChevronRight, User, Users } from "lucide-react";
import StreamingMarkdown from "./StreamingMarkdown";
import DebateTimeline from "./DebateTimeline";
import CopyButton from "./CopyButton";
import { markdownComponents } from "./DebateProgress";
import { buildDebateThread, ContextNote, ThreadComment, ThreadReply } from "../utils/debateThread";
import { buildMultiStages, buildSingleStages, expertLabel, joinNames } from "../utils/debateStages";
import { getExpertPalette } from "../utils/expertStyles";
import VerdictMeter, { ExpertLeanTag } from "./VerdictMeter";
import { AnswerFormat, Verdict, leanOptions } from "../utils/verdict";
import { DebateSource, sourcesForStep, visibleSources } from "../utils/sources";
import SourcesDisclosure from "./SourcesList";

interface DebateThreadViewProps {
  debateTopic: string | null;
  debateImages?: string[];
  councilProgress: any[];
  chaosProgress: any[];
  councilFinal: string | null;
  facilitatorDone: boolean;
  isLoading: boolean;
  selectedExperts: string[];
  debateMode?: "single" | "multi";
  /** Who asked, when it isn't the person reading (the public feed). */
  askerName?: string;
  /** The panel verdict on a question with fixed answers. */
  verdict?: Verdict | null;
  /** The answer format, for labelling expert leans before the verdict exists. */
  answerFormat?: AnswerFormat | null;
  /** Links the experts relied on. */
  sources?: DebateSource[];
}

const ROLE_AVATAR: Record<string, string> = {
  chaos: "border-accent-line bg-action text-on-solid",
  fact_checker: "border-success-line bg-success-soft text-success",
  analyst: "border-accent-line bg-expert-teal-soft text-accent",
  you: "border-line bg-canvas text-content",
};

function initials(label: string) {
  const words = label.split(/\s+/).filter(Boolean);
  return (words.length > 1 ? words[0][0] + words[1][0] : label.slice(0, 2)).toUpperCase();
}

function Avatar({ who, panel, size = 28 }: { who: string; panel: string[]; size?: number }) {
  const label = who === "you" ? "You" : expertLabel(who);
  if (who === "you") {
    return (
      <span aria-hidden="true" className={`thread-avatar ${ROLE_AVATAR.you}`} style={{ width: size, height: size }}>
        <User className="h-3.5 w-3.5" />
      </span>
    );
  }
  const palette = getExpertPalette(who, panel);
  const tone = ROLE_AVATAR[who] ?? (panel.includes(who) ? `${palette.border} ${palette.headerBg} ${palette.accentText}` : ROLE_AVATAR.you);
  return (
    <span aria-hidden="true" className={`thread-avatar ${tone}`} style={{ width: size, height: size, fontSize: "var(--text-caption)" }}>
      {initials(label)}
    </span>
  );
}

function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1" aria-hidden="true">
      <span className="typing-dot" />
      <span className="typing-dot" />
      <span className="typing-dot" />
    </span>
  );
}

/**
 * Long markdown clamped to a fixed height (shorter on phones). The preview
 * always shows the start, even while the text is still streaming.
 */
function ClampedMarkdown({
  text,
  streaming,
  clamp,
  proseClass = "text-sm",
  moreLabel = "Show more",
}: {
  text: string;
  streaming: boolean;
  clamp: string;
  proseClass?: string;
  moreLabel?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || expanded) return;
    const measure = () => setOverflows(el.scrollHeight > el.clientHeight + 2);
    measure();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    const content = el.firstElementChild;
    if (observer && content) observer.observe(content);
    return () => observer?.disconnect();
  }, [expanded, text]);

  return (
    <div>
      <div
        ref={ref}
        className={`thread-clamp ${expanded ? "is-expanded" : ""} ${overflows && !expanded ? "is-faded" : ""}`}
        style={{ "--clamp": clamp } as React.CSSProperties}
      >
        <StreamingMarkdown
          text={text}
          streaming={streaming}
          components={markdownComponents}
          className={`thread-md ${proseClass} max-w-full break-words leading-relaxed text-content`}
        />
      </div>
      {(overflows || expanded) && (
        <button type="button" className="thread-text-button mt-1" aria-expanded={expanded} onClick={() => setExpanded((v) => !v)}>
          {expanded ? "Show less" : moreLabel}
        </button>
      )}
    </div>
  );
}

function SkeletonLines() {
  return (
    <div className="mt-1 space-y-2" aria-hidden="true">
      <span className="skeleton block h-3 w-11/12" />
      <span className="skeleton block h-3 w-4/5" />
      <span className="skeleton block h-3 w-2/3" />
    </div>
  );
}

function Reply({ reply, target, panel, sources }: { reply: ThreadReply; target: string; panel: string[]; sources: DebateSource[] }) {
  return (
    <li className="thread-reply motion-rise">
      <Avatar who={reply.author} panel={panel} size={24} />
      <div className="min-w-0">
        <p className="flex flex-wrap items-center gap-x-1.5 text-label leading-6">
          <span className="font-semibold text-content">{expertLabel(reply.author)}</span>
          <span className="text-muted">{reply.toPanel ? "to the panel" : `replying to ${expertLabel(target)}`}</span>
          {reply.kind === "chaos" && <span className="thread-tag">Chaos challenge</span>}
          {reply.kind === "fact_check" && <span className="thread-tag">Fact check</span>}
          {reply.streaming && <TypingDots />}
        </p>
        <ClampedMarkdown text={reply.text} streaming={reply.streaming} clamp="11rem" />
        <SourcesDisclosure sources={sourcesForStep(reply.sourceIds, sources)} className="mt-1.5" />
      </div>
    </li>
  );
}

function VersionToggle({
  showOriginal,
  onChange,
  revising,
  label,
}: {
  showOriginal: boolean;
  onChange: (original: boolean) => void;
  revising: boolean;
  label: string;
}) {
  return (
    <div className="seg-control thread-version" role="group" aria-label={`${label}'s argument version`}>
      <button type="button" className="seg-item" aria-pressed={showOriginal} onClick={() => onChange(true)}>
        Original
      </button>
      <button type="button" className="seg-item" aria-pressed={!showOriginal} onClick={() => onChange(false)}>
        {revising && <span className="thread-live-dot" aria-hidden="true" />}
        Revised
      </button>
    </div>
  );
}

function Comment({
  comment,
  mode,
  panel,
  collapsed,
  repliesHidden,
  showOriginal,
  onToggleCollapsed,
  onToggleReplies,
  onShowOriginal,
  leanOpts,
  sources,
}: {
  comment: ThreadComment;
  mode: "single" | "multi";
  panel: string[];
  leanOpts: Array<{ key: string; label: string }>;
  collapsed: boolean;
  repliesHidden: boolean;
  showOriginal: boolean;
  onToggleCollapsed: () => void;
  onToggleReplies: () => void;
  onShowOriginal: (original: boolean) => void;
  sources: DebateSource[];
}) {
  const label = expertLabel(comment.expert);
  const hasRevision = !!comment.revised;
  const viewingOriginal = !hasRevision || showOriginal;
  const text = viewingOriginal ? comment.original : comment.revised!;
  const streaming = viewingOriginal ? comment.originalStreaming : comment.revisedStreaming;
  const lean = viewingOriginal ? comment.openingLean : comment.revisedLean;
  const versionSources = sourcesForStep(viewingOriginal ? comment.openingSourceIds : comment.revisedSourceIds, sources);
  const replyCount = comment.replies.length;
  const headingId = `thread-${comment.expert}-name`;
  const showReplies = !repliesHidden && replyCount > 0;
  const showTree = !collapsed && (showReplies || comment.replying.length > 0);

  const status = comment.researching
    ? "Gathering research"
    : comment.revised !== null && comment.revisedStreaming
      ? comment.revised
        ? "Revising"
        : "Revising, original shown"
      : comment.originalStreaming
        ? "Writing"
        : hasRevision
          ? mode === "single"
            ? "Final answer after the chaos challenge and fact check"
            : "Revised after cross-examination"
          : comment.original
            ? mode === "single"
              ? "First analysis"
              : "Opening argument"
            : "Waiting to start";

  return (
    <article id={`thread-${comment.expert}`} className="thread-comment" aria-labelledby={headingId}>
      <div className="thread-gutter">
        <Avatar who={comment.expert} panel={panel} />
        {showTree && (
          <button
            type="button"
            className="thread-line"
            aria-expanded={true}
            aria-label={`Collapse ${label}'s thread`}
            onClick={onToggleCollapsed}
          />
        )}
      </div>
      <div className="min-w-0">
        <header className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <h3 id={headingId} className="text-sm font-semibold text-content">{label}</h3>
          <span className="text-xs text-muted">{status}</span>
          {lean && leanOpts.length > 0 && (
            <ExpertLeanTag lean={lean} opening={viewingOriginal ? null : comment.openingLean} options={leanOpts} />
          )}
          {comment.model && (
            <span className="max-w-[14rem] truncate font-mono text-xs text-muted" title={comment.model}>
              {comment.model}
            </span>
          )}
          {hasRevision && !collapsed && (
            <span className="ml-auto">
              <VersionToggle showOriginal={showOriginal} onChange={onShowOriginal} revising={comment.revisedStreaming} label={label} />
            </span>
          )}
        </header>

        {collapsed ? (
          <button type="button" className="thread-collapsed" aria-expanded={false} onClick={onToggleCollapsed}>
            <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />
            Expand thread
            {replyCount > 0 && <span className="tabular-nums text-muted">· {replyCount} {replyCount === 1 ? "reply" : "replies"}</span>}
          </button>
        ) : (
          <>
            <div className="mt-1">
              {text ? (
                <ClampedMarkdown text={text} streaming={streaming} clamp="17rem" moreLabel="Show full argument" />
              ) : (
                <SkeletonLines />
              )}
            </div>

            <SourcesDisclosure key={viewingOriginal ? "original" : "revised"} sources={versionSources} className="mt-2" />

            {replyCount > 0 && (
              <button type="button" className="thread-text-button mt-2" aria-expanded={!repliesHidden} onClick={onToggleReplies}>
                <ChevronDown aria-hidden="true" className={`h-3.5 w-3.5 transition-transform duration-150 ${repliesHidden ? "-rotate-90" : ""}`} />
                <span className="tabular-nums">
                  {repliesHidden ? "Show" : "Hide"} {replyCount} {replyCount === 1 ? "reply" : "replies"}
                </span>
              </button>
            )}
          </>
        )}
      </div>

      {showTree && (
        <ul className="thread-replies thread-tree">
          {showReplies &&
            comment.replies.map((reply) => (
              <Reply key={reply.id} reply={reply} target={comment.expert} panel={panel} sources={sources} />
            ))}
          {comment.replying.length > 0 && (
            <li className="thread-reply text-label leading-6 text-muted" role="status">
              <span className="flex h-6 items-center justify-center" aria-hidden="true">
                <TypingDots />
              </span>
              <span>
                {joinNames(comment.replying.map(expertLabel))} {comment.replying.length === 1 ? "is" : "are"} replying
              </span>
            </li>
          )}
        </ul>
      )}
    </article>
  );
}

function ContextRow({ note, panel, sources }: { note: ContextNote; panel: string[]; sources: DebateSource[] }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-line first:border-t-0">
      <button type="button" className="thread-context-toggle" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
        <ChevronRight aria-hidden="true" className="disclosure-chevron h-3.5 w-3.5 text-muted" />
        <Avatar who={note.author} panel={panel} size={20} />
        <span className="font-medium text-content">{note.title}</span>
        {note.streaming && <TypingDots />}
      </button>
      {open && (
        <div className="motion-fade pb-3 pl-[52px]">
          <ClampedMarkdown text={note.text} streaming={note.streaming} clamp="20rem" />
          <SourcesDisclosure sources={sourcesForStep(note.sourceIds, sources)} className="mt-2" />
        </div>
      )}
    </div>
  );
}

/**
 * The debate as a social thread: the question is the post, each expert's
 * opening argument is a comment, attacks aimed at an expert are replies under
 * it, and the revision replaces the comment as an edit.
 */
export default function DebateThreadView({
  debateTopic,
  debateImages = [],
  councilProgress,
  chaosProgress,
  councilFinal,
  facilitatorDone,
  isLoading,
  selectedExperts,
  debateMode = "multi",
  askerName = "You",
  verdict = null,
  answerFormat = null,
  sources = [],
}: DebateThreadViewProps) {
  const leanOpts = debateMode === "multi" ? leanOptions(verdict, answerFormat) : [];
  const thread = useMemo(
    () =>
      buildDebateThread({ councilProgress, chaosProgress, councilFinal, facilitatorDone, isLoading, selectedExperts, debateMode }),
    [councilProgress, chaosProgress, councilFinal, facilitatorDone, isLoading, selectedExperts, debateMode],
  );
  const stageInput = { councilProgress, chaosProgress, councilFinal, facilitatorDone, isLoading, selectedExperts };
  const stages = debateMode === "single" ? buildSingleStages(stageInput) : buildMultiStages(stageInput);

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [repliesHidden, setRepliesHidden] = useState<Record<string, boolean>>({});
  const [showOriginal, setShowOriginal] = useState<Record<string, boolean>>({});
  const [railOpen, setRailOpen] = useState(false);

  const noteRef = useRef<HTMLElement>(null);
  const [noteVisible, setNoteVisible] = useState(true);
  const hasSynthesis = !!thread.synthesis;
  useEffect(() => {
    const el = noteRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => setNoteVisible(entry.isIntersecting), { threshold: 0.05 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasSynthesis]);

  const panel = debateMode === "single" ? ["analyst"] : selectedExperts;
  const withReplies = thread.comments.filter((c) => c.replies.length > 0);
  const allRepliesHidden = withReplies.length > 0 && withReplies.every((c) => repliesHidden[c.expert]);
  const toggleAllReplies = () => {
    const hide = !allRepliesHidden;
    setRepliesHidden(Object.fromEntries(thread.comments.map((c) => [c.expert, hide])));
  };

  const doneCount = stages.filter((s) => s.status === "done").length;
  const activeStage = stages.find((s) => s.status === "active") ?? (isLoading ? stages.find((s) => s.status === "pending") : undefined);
  const summary = activeStage ? activeStage.label : doneCount === stages.length && stages.length > 0 ? "Debate complete" : "Starting";

  const jumpTo = (id: string, expert?: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    // Commit the closed progress sheet and expanded thread first, so the
    // scroll target is measured after the layout above it has shifted.
    flushSync(() => {
      setRailOpen(false);
      if (expert) setCollapsed((c) => ({ ...c, [expert]: false }));
    });
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const argumentCount = thread.comments.filter((c) => c.original).length;
  const allSources = visibleSources(sources);

  return (
    <div className="debate-layout">
      {stages.length > 0 && (
        <aside className={`thread-rail ${railOpen ? "is-open" : ""}`} aria-label="Debate progress">
          <button type="button" className="thread-rail-summary" aria-expanded={railOpen} onClick={() => setRailOpen((v) => !v)}>
            <span className="flex min-w-0 items-center gap-2">
              {isLoading && <span className="thread-live-dot" aria-hidden="true" />}
              <span className="truncate font-medium text-content">{summary}</span>
            </span>
            <span className="flex items-center gap-1.5 text-muted">
              <span className="font-mono tabular-nums text-xs">
                {doneCount}/{stages.length}
              </span>
              <ChevronDown aria-hidden="true" className={`h-4 w-4 transition-transform duration-150 ${railOpen ? "rotate-180" : ""}`} />
            </span>
          </button>
          <div className="thread-rail-body">
            <DebateTimeline stages={stages} isLoading={isLoading} title={debateMode === "single" ? "Analysis progress" : "Council progress"} />
            {debateMode === "multi" && thread.comments.length > 0 && (
              <nav aria-label="Jump to an expert" className="mt-5">
                <h2 className="mb-1.5 text-label font-semibold text-content">Panel</h2>
                <ul className="space-y-0.5">
                  {thread.comments.map((c) => (
                    <li key={c.expert}>
                      <button type="button" className="thread-panel-link" onClick={() => jumpTo(`thread-${c.expert}`, c.expert)}>
                        <Avatar who={c.expert} panel={panel} size={20} />
                        <span className="min-w-0 flex-1 truncate">{expertLabel(c.expert)}</span>
                        {c.replies.length > 0 && <span className="font-mono text-xs tabular-nums text-muted">{c.replies.length}</span>}
                      </button>
                    </li>
                  ))}
                </ul>
              </nav>
            )}
          </div>
        </aside>
      )}

      <div className="debate-layout-main min-w-0">
        {debateTopic && (
          <header className="pb-4" style={{ viewTransitionName: "debate-topic" } as React.CSSProperties}>
            <div className="flex items-center gap-2">
              <Avatar who="you" panel={panel} />
              <span className="text-sm font-semibold text-content">{askerName}</span>
              <span className="text-xs text-muted">asked the {debateMode === "single" ? "analyst" : "council"}</span>
            </div>
            <p className="mt-2 whitespace-pre-wrap text-base font-semibold leading-snug tracking-tight text-content">{debateTopic}</p>
            {debateImages.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {debateImages.map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`Debate attachment ${idx + 1}`}
                    className="max-h-48 max-w-full rounded-lg border border-line bg-surface object-contain"
                  />
                ))}
              </div>
            )}
          </header>
        )}

        {thread.synthesis ? (
          <section ref={noteRef} id="thread-synthesis" className="thread-note" aria-labelledby="thread-synthesis-title">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <Users aria-hidden="true" className="h-4 w-4 text-accent" />
              <h2 id="thread-synthesis-title" className="text-sm font-semibold text-content">
                Council synthesis
              </h2>
              <span className="text-xs text-muted">
                {thread.synthesis.streaming ? "Facilitator writing" : `From ${selectedExperts.length} experts after cross-examination and revision`}
              </span>
              {!thread.synthesis.streaming && (
                <span className="ml-auto">
                  <CopyButton label="Copy synthesis" getText={() => thread.synthesis!.text} />
                </span>
              )}
            </div>
            {verdict && (
              <div className="mt-3 mb-4">
                <h3 className="mb-2 text-label font-medium text-muted">Panel verdict</h3>
                <VerdictMeter verdict={verdict} />
              </div>
            )}
            <div className="mt-2">
              <ClampedMarkdown
                text={thread.synthesis.text}
                streaming={thread.synthesis.streaming}
                clamp="24rem"
                proseClass="text-sm sm:text-sm"
                moreLabel="Show full synthesis"
              />
            </div>
          </section>
        ) : thread.synthesisPending ? (
          <section className="thread-note is-pending" aria-label="Council synthesis">
            <div className="flex items-center gap-2">
              <Users aria-hidden="true" className="h-4 w-4 text-muted" />
              <h2 className="text-sm font-semibold text-muted">Council synthesis</h2>
            </div>
            {verdict ? (
              <div className="mt-3">
                <h3 className="mb-2 text-label font-medium text-muted">Panel verdict</h3>
                <VerdictMeter verdict={verdict} />
                <p className="mt-3 text-label text-muted">The facilitator is writing the synthesis.</p>
              </div>
            ) : (
              <p className="mt-1 text-label text-muted">Appears here once every expert has revised their position.</p>
            )}
          </section>
        ) : null}

        {thread.context.length > 0 && (
          <div className="mt-4 rounded-lg border border-line">
            {thread.context.map((note) => (
              <ContextRow key={note.key} note={note} panel={panel} sources={sources} />
            ))}
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-3 py-2 text-label">
          <span className="tabular-nums text-muted">
            {argumentCount} {argumentCount === 1 ? "argument" : "arguments"} · {thread.replyCount} {thread.replyCount === 1 ? "reply" : "replies"}
          </span>
          {withReplies.length > 0 && (
            <button type="button" className="thread-text-button" onClick={toggleAllReplies}>
              {allRepliesHidden ? "Show all replies" : "Collapse all replies"}
            </button>
          )}
        </div>
        {!isLoading && <SourcesDisclosure sources={allSources} label="All sources" className="mb-3" />}

        <div style={{ viewTransitionName: "debate-panel" } as React.CSSProperties}>
          {thread.comments.map((comment) => (
            <Comment
              key={comment.expert}
              comment={comment}
              mode={debateMode}
              panel={panel}
              collapsed={!!collapsed[comment.expert]}
              repliesHidden={!!repliesHidden[comment.expert]}
              showOriginal={!!showOriginal[comment.expert]}
              onToggleCollapsed={() => setCollapsed((c) => ({ ...c, [comment.expert]: !c[comment.expert] }))}
              onToggleReplies={() => setRepliesHidden((r) => ({ ...r, [comment.expert]: !r[comment.expert] }))}
              onShowOriginal={(original) => setShowOriginal((s) => ({ ...s, [comment.expert]: original }))}
              leanOpts={leanOpts}
              sources={sources}
            />
          ))}

          {thread.panelReplies.length > 0 && (
            <ul className="thread-replies border-t border-line pt-4">
              {thread.panelReplies.map((reply) => (
                <Reply key={reply.id} reply={reply} target="" panel={panel} sources={sources} />
              ))}
            </ul>
          )}

          {thread.comments.length === 0 && !isLoading && (
            <p className="border-t border-line py-6 text-sm text-muted">Start a debate to see the panel's arguments here.</p>
          )}
        </div>

        {hasSynthesis && !noteVisible && (
          <div className="thread-jump">
            <button type="button" className="thread-jump-button" onClick={() => jumpTo("thread-synthesis")}>
              <ArrowUp aria-hidden="true" className="h-4 w-4" />
              {thread.synthesis!.streaming ? "Synthesis is being written" : "Read the synthesis"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export type DebateView = "thread" | "classic";

const VIEW_KEY = "debate-view";

/** Which debate layout this browser last chose; the thread is the default. */
export function useDebateView(): [DebateView, (view: DebateView) => void] {
  const [view, setView] = useState<DebateView>(() => {
    try {
      return window.localStorage.getItem(VIEW_KEY) === "classic" ? "classic" : "thread";
    } catch {
      return "thread";
    }
  });
  useEffect(() => {
    try {
      window.localStorage.setItem(VIEW_KEY, view);
    } catch {
      // Storage can be unavailable (private mode); the choice then lasts for this visit.
    }
  }, [view]);
  return [view, setView];
}

export function DebateViewSwitch({ value, onChange }: { value: DebateView; onChange: (view: DebateView) => void }) {
  return (
    <div className="mb-3 flex justify-end">
      <div className="seg-control thread-version" role="group" aria-label="Debate layout">
        <button type="button" className="seg-item" aria-pressed={value === "thread"} onClick={() => onChange("thread")}>
          Thread
        </button>
        <button type="button" className="seg-item" aria-pressed={value === "classic"} onClick={() => onChange("classic")}>
          Classic
        </button>
      </div>
    </div>
  );
}
